import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { displayNameFor, shouldRenderHandlePill, handleFor } from '@/lib/displayName';
import { MessageSquare, Send, Loader2, User as UserIcon, AtSign, CheckCircle2, Clock } from 'lucide-react';
import MentionAutocomplete from '@/components/MentionAutocomplete';

export default function ClientMessageCenter() {
  const { api, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [mentions, setMentions] = useState([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);

  const isPmOrAdmin = ['master_admin', 'production_manager', 'supervisor', 'creative_director'].includes(user?.role);

  useEffect(() => {
    (async () => {
      try {
        if (!isPmOrAdmin) {
          try {
            const dashRes = await api.get('/vendor/my-dashboard');
            const myProjects = dashRes.data?.my_projects || [];
            if (myProjects.length > 0) {
              const list = myProjects.map(p => ({ id: p.project_id, name: p.project_name }));
              setProjects(list);
              if (list.length && !projectId) setProjectId(list[0].id);
              return;
            }
          } catch {}
        }
        const { data } = await api.get('/projects');
        const list = data || [];
        setProjects(list);
        if (list.length && !projectId) setProjectId(list[0].id);
      } catch {}
    })();
  }, []);

  const refresh = async (pid = projectId) => {
    if (!pid) return;
    setLoading(true);
    try {
      const r = await api.get(`/messages/thread?project_id=${pid}`);
      setMessages(r.data?.messages || []);
    } catch { setMessages([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [projectId]);

  // Auto-poll every 12s so new replies feel near-real-time (was 3s —
  // perf rewrite May 24, 2026: 3s polling across 5 client tabs was
  // generating ~100 req/min just from clients). Backed off when tab is
  // hidden. Refresh fires immediately on visibility-return so re-opening
  // the tab feels instant.
  useEffect(() => {
    if (!projectId) return;
    let stopped = false;
    const tick = () => {
      if (stopped || document.hidden) return;
      refresh(projectId);
    };
    const t = setInterval(tick, 12000);
    const onVis = () => { if (!document.hidden) refresh(projectId); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true; clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [projectId]);

  const send = async () => {
    const txt = draft.trim();
    if (!txt || !projectId) return;
    setSending(true);
    try {
      await api.post('/messages/post', { project_id: projectId, body: txt });
      setDraft('');
      await refresh(projectId);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const loadMentions = async () => {
    setMentionsLoading(true);
    try {
      const r = await api.get('/mentions');
      setMentions(r.data || []);
    } catch { setMentions([]); }
    finally { setMentionsLoading(false); }
  };

  const markRead = async (id) => {
    try {
      await api.put(`/mentions/${id}/read`);
      setMentions(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
    } catch {}
  };

  useEffect(() => {
    if (activeTab === 'mentions') loadMentions();
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6 max-w-4xl mx-auto" data-testid="client-message-center">
      <div className="mb-5">
        <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <MessageSquare className="h-7 w-7 text-violet-400" />
          Message Center
        </h1>
        <p className="text-sm text-zinc-400 mt-1">Direct chat with the production team &amp; @mention notifications.</p>
      </div>

      {/* Tabs: Messages | @Mentions */}
      <div className="flex gap-1 border-b border-zinc-800 mb-4" data-testid="message-center-tabs">
        <button onClick={() => setActiveTab('messages')} data-testid="tab-messages"
          className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 ${activeTab === 'messages' ? 'border-violet-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          <MessageSquare className="w-3.5 h-3.5" /> Messages
        </button>
        <button onClick={() => setActiveTab('mentions')} data-testid="tab-mentions"
          className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 ${activeTab === 'mentions' ? 'border-amber-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          <AtSign className="w-3.5 h-3.5" /> @Mentions
          {mentions.filter(m => !m.read).length > 0 && (
            <span className="ml-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white font-bold flex items-center justify-center">{mentions.filter(m => !m.read).length}</span>
          )}
        </button>
      </div>

      {/* @Mentions Tab */}
      {activeTab === 'mentions' && (
        <div className="space-y-2" data-testid="mentions-tab">
          {mentionsLoading ? (
            <div className="text-center py-12 text-zinc-500 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading mentions...</div>
          ) : mentions.length === 0 ? (
            <div className="text-center py-12"><AtSign className="w-8 h-8 text-zinc-700 mx-auto mb-2" /><p className="text-xs text-zinc-500">No @mentions yet</p></div>
          ) : mentions.map(m => (
            <div key={m.id} className={`flex items-start gap-3 px-4 py-3 rounded-md border transition-colors ${m.read ? 'bg-zinc-900/50 border-zinc-800' : 'bg-amber-500/5 border-amber-500/20'}`}
              data-testid={`mention-${m.id}`}>
              <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-zinc-300">{(m.from_name || '?')[0].toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white font-semibold">{m.from_name || m.from_email}</span>
                  <span className="text-[10px] text-zinc-500">{m.project_name}</span>
                  <span className="text-[10px] text-zinc-600 font-mono">{m.created_at ? new Date(m.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                </div>
                <p className="text-xs text-zinc-300 mt-1">{m.body || m.message}</p>
              </div>
              {!m.read && (
                <button onClick={() => markRead(m.id)} className="h-6 px-2 text-[9px] bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30 flex items-center gap-1 flex-shrink-0">
                  <CheckCircle2 className="w-3 h-3" /> Read
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (<>

      {/* Project selector */}
      {projects.length > 0 && (
        <div className="mb-4">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Project</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            data-testid="msg-project-picker"
            className="w-full md:w-80 h-9 bg-zinc-900 border border-zinc-800 rounded-md px-3 text-sm text-white focus:outline-none">
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.is_show ? '📺 ' : ''}{p.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Thread */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 flex flex-col" style={{ minHeight: '60vh' }} data-testid="msg-thread">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '60vh' }}>
          {loading && messages.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm" data-testid="msg-empty">
              No messages yet — start the conversation 👇
            </div>
          ) : (
            messages.map(m => {
              const mine = m.author_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
                  <div className={`max-w-[75%] rounded-lg px-3 py-2 ${
                    mine ? 'bg-violet-500 text-white' : 'bg-zinc-800 text-zinc-200'
                  }`}>
                    {!mine && (
                      <div className="flex items-center gap-1.5 mb-1 text-[10px] text-zinc-400">
                        <UserIcon className="w-3 h-3" />
                        <span className="font-semibold">{displayNameFor(m, user?.role) || 'Production team'}</span>
                        {shouldRenderHandlePill(m, user?.role) && <span className="font-mono text-cyan-400">@{handleFor(m)}</span>}
                        {m.author_role && <span>· {m.author_role.replace(/_/g, ' ')}</span>}
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap break-words">{renderMentionAware(m.body)}</p>
                    <p className={`text-[9px] mt-1 ${mine ? 'text-violet-100/70' : 'text-zinc-500'}`}>
                      {new Date(m.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-zinc-800 p-3 flex items-end gap-2">
          <div className="flex-1">
            <MentionAutocomplete
              api={api}
              projectId={projectId}
              value={draft}
              onChange={setDraft}
              dataTestid="msg-composer"
              onEnterSubmit={send}
              placeholder="Type a message… use @name to tag someone (Cmd/Ctrl + Enter to send)"
            />
          </div>
          <button onClick={send} disabled={sending || !draft.trim() || !projectId}
            data-testid="msg-send-btn"
            className="h-10 px-4 rounded-md bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send
          </button>
        </div>
      </div>
      </>)}
    </div>
  );
}

// Render text with @mentions highlighted as chips. Pure presentational.
function renderMentionAware(text) {
  if (!text) return '';
  const re = /@([A-Za-z0-9][A-Za-z0-9._\-]{1,40})/g;
  const parts = [];
  let last = 0; let m; let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span key={`mention-${i++}`} className="bg-violet-500/30 text-violet-100 rounded px-1 font-medium">
        @{m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}
